import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import PdfViewerWrapper from './pdf-viewer.styled.js';

// Configure PDF.js worker for v2
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PdfViewer = ({
  url,
  onScrollToEnd,
  token,
  className
}) => {
  const [numPages, setNumPages] = useState(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const hasScrolledToEndRef = useRef(false);
  const checkIntervalRef = useRef(null);
  const lastCallbackRef = useRef(onScrollToEnd);

  // Get full URL with token
  const fullUrl = token ? `${url}?token=${token}` : url;

  // Reset scroll state when URL changes
  useEffect(() => {
    hasScrolledToEndRef.current = false;
  }, [fullUrl]);

  // Keep callback ref updated
  useEffect(() => {
    lastCallbackRef.current = onScrollToEnd;
  }, [onScrollToEnd]);


  // Handle PDF load success
  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
  }, []);

  // Handle container resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Handle scroll detection
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      const isScrollable = scrollHeight > clientHeight;

      // Check if scrolled to bottom (with 100px tolerance)
      // For non-scrollable documents, require that user has scrolled at least once (scrollTop > 0)
      const reachedEnd = distanceFromBottom < 100 && (isScrollable ? true : scrollTop > 0);

      if (reachedEnd) {
        if (!hasScrolledToEndRef.current) {
          hasScrolledToEndRef.current = true;
          if (lastCallbackRef.current) {
            lastCallbackRef.current();
          }
        }
        // Stop the interval once we've reached the end
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
      }
    };


    container.addEventListener('scroll', handleScroll);

    // Check after PDF pages are loaded - use timeout to ensure rendering is complete
    checkIntervalRef.current = setInterval(() => {
      handleScroll();
    }, 500);

    // Check initial state immediately
    setTimeout(handleScroll, 100);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [numPages]);

  return (
    <PdfViewerWrapper ref={containerRef} className={className}>
      <Document
        file={fullUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={
          <div className="pdf-loading">
            <div className="spinner" />
            <p>Načítavam dokument...</p>
          </div>
        }
        error={
          <div className="pdf-error">
            <p>Nepodarilo sa načítať dokument.</p>
          </div>
        }
      >
        {Array.from(new Array(numPages), (el, index) => (
          <Page
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            width={containerWidth ? containerWidth - 40 : undefined}
            className="pdf-page"
          />
        ))}
      </Document>

      {numPages && (
        <div className="pdf-page-info">
          Celkovo {numPages} {numPages === 1 ? 'strana' : numPages < 5 ? 'strany' : 'strán'}
        </div>
      )}
    </PdfViewerWrapper>
  );

};

export default PdfViewer;

